import type { User } from "@clerk/backend";
import { getEasyGptMarketplaceOrgId } from "~/lib/marketplace/constants";

export const HIRE_INTENT_TTL_MS = 72 * 60 * 60 * 1000;

export function marketplaceHireIntentExpiresAt(fromMs = Date.now()): Date {
  return new Date(fromMs + HIRE_INTENT_TTL_MS);
}

export function isMarketplaceHireIntentExpired(
  intent: { status: string; expiresAt: Date },
  now = new Date(),
): boolean {
  return intent.status === "EXPIRED" || intent.expiresAt <= now;
}

/** Prefer server message over generic HTTP status text (e.g. "Bad Request"). */
export function getTrpcErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== "object") {
    return fallback;
  }

  const trpcError = error as {
    message?: string;
    data?: {
      message?: string;
      code?: string;
      zodError?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] };
    };
    shape?: { message?: string };
  };

  const zodFieldErrors = trpcError.data?.zodError?.fieldErrors;
  if (zodFieldErrors && typeof zodFieldErrors === "object") {
    for (const messages of Object.values(zodFieldErrors)) {
      const first = messages?.find((value) => typeof value === "string" && value.trim());
      if (first) {
        return first.trim();
      }
    }
  }

  const zodFormErrors = trpcError.data?.zodError?.formErrors;
  if (Array.isArray(zodFormErrors)) {
    const first = zodFormErrors.find((value) => typeof value === "string" && value.trim());
    if (first) {
      return first.trim();
    }
  }

  const candidates = [
    trpcError.data?.message,
    trpcError.shape?.message,
    trpcError.message,
  ].filter((value): value is string => typeof value === "string" && value.trim().length > 0);

  for (const message of candidates) {
    const normalized = message.trim();
    if (
      normalized !== "Bad Request" &&
      normalized !== "Forbidden" &&
      normalized !== "Not Found" &&
      normalized !== "Internal Server Error"
    ) {
      return normalized;
    }
  }

  if (trpcError.data?.code === "BAD_REQUEST") {
    return fallback;
  }

  return candidates[0]?.trim() || fallback;
}

export function normalizeMarketplaceHireEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Stable placeholder email for pre–sign-up hire preview (claimed on Sign Up). */
export function buildGuestPreviewHireEmail(guestSessionId: string): string {
  const id =
    guestSessionId.replace(/[^a-zA-Z0-9]/g, "").slice(0, 32) || "anon";
  return `guest+${id}@preview.easygpt.builders`;
}

export function isGuestPreviewHireEmail(email: string): boolean {
  const normalized = normalizeMarketplaceHireEmail(email);
  return (
    normalized.startsWith("guest+") &&
    normalized.endsWith("@preview.easygpt.builders")
  );
}

function isAlreadyOrgMemberError(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("already a member") ||
    normalized.includes("already belongs") ||
    normalized.includes("already in this organization")
  );
}

export function primaryClerkEmailAddress(clerkUser: User): string | undefined {
  return (
    clerkUser.emailAddresses.find((e) => e.id === clerkUser.primaryEmailAddressId)
      ?.emailAddress ?? clerkUser.emailAddresses[0]?.emailAddress
  );
}

/** True when any email on the Clerk user matches the hire intent email (not primary-only). */
export function clerkUserMatchesMarketplaceHireEmail(
  clerkUser: User,
  intentEmail: string,
): boolean {
  const target = normalizeMarketplaceHireEmail(intentEmail);
  return clerkUser.emailAddresses.some(
    (addr) => normalizeMarketplaceHireEmail(addr.emailAddress) === target,
  );
}

export function marketplaceHireEmailMismatchMessage(
  intentEmail: string,
  clerkUser: User,
): string {
  const signedInAs = primaryClerkEmailAddress(clerkUser) ?? "another account";
  return `Sign in with ${intentEmail} (the email you used to start this hire). You are currently signed in as ${signedInAs}.`;
}

export function formatMarketplaceHireProvisionError(error: string): string {
  const trimmed = error.trim();
  const normalized = trimmed.toLowerCase();
  if (!normalized || normalized === "bad request") {
    return "We could not link this agent to your account. Try signing out and back in, or contact support if this continues.";
  }
  if (isAlreadyOrgMemberError(trimmed)) {
    return "Your account is already linked to the Inbound GPT organization, but we could not finish setup. Try refreshing the page or contact support if this continues.";
  }
  if (normalized.includes("organization") || normalized.includes("membership")) {
    return "We could not link this agent to your account. Try signing out and back in, or contact support if this continues.";
  }
  return trimmed;
}

export function getMarketplaceHirePortalOrgId(): string {
  const orgId = getEasyGptMarketplaceOrgId();
  if (!orgId) {
    throw new Error(
      "Marketplace hire portal organization is not configured (CLERK_INBOUND_GPT_ORGANIZATION_ID).",
    );
  }
  return orgId;
}

export function getMarketplaceHireOwnerUserId(fallbackUserId: string): string {
  const serviceUserId = process.env.MARKETPLACE_HIRE_SERVICE_CLERK_USER_ID?.trim();
  return serviceUserId || fallbackUserId;
}

/** Session key while signup is in progress — used to resume hire after email-link verification. */
export const PENDING_HIRE_INTENT_SESSION_KEY = "egpt_pending_hire_intent_id";

export function readPendingHireIntentId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(PENDING_HIRE_INTENT_SESSION_KEY)?.trim() || null;
  } catch {
    return null;
  }
}

export function writePendingHireIntentId(hireIntentId: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(PENDING_HIRE_INTENT_SESSION_KEY, hireIntentId);
  } catch {
    /* ignore */
  }
}

export function clearPendingHireIntentId(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(PENDING_HIRE_INTENT_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function buildMarketplaceHireCompletionPath(hireIntentId: string): string {
  return `/projects/hire?hireIntentId=${encodeURIComponent(hireIntentId)}`;
}

/** Restore custom hire wizard (Step 6 Summary) for an in-progress preview session. */
export function buildMarketplaceHireResumePath(hireIntentId: string): string {
  return `/agents/hire/custom?hireIntentId=${encodeURIComponent(hireIntentId)}`;
}

/** Restores custom hire preview after Stripe checkout cancel (full page reload). */
export const HIRE_CUSTOM_PREVIEW_SESSION_KEY = "egpt_hire_custom_preview_state";

export type HireCustomPreviewSessionState = {
  previewProjectId: string;
  previewHireIntentId: string;
  /** Marketplace template cloned from — used to discard stale preview after switching agents. */
  templateProjectId?: string;
  /** Survive Google OAuth full-page return — keep confirmed agent identity / avatar. */
  agentName?: string;
  jobTitle?: string;
  profileImageUrl?: string;
  agentNameConfirmed?: boolean;
};

export function readHireCustomPreviewState(): HireCustomPreviewSessionState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      sessionStorage.getItem(HIRE_CUSTOM_PREVIEW_SESSION_KEY) ||
      localStorage.getItem(HIRE_CUSTOM_PREVIEW_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HireCustomPreviewSessionState;
    if (!parsed.previewProjectId || !parsed.previewHireIntentId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeHireCustomPreviewState(state: HireCustomPreviewSessionState): void {
  if (typeof window === "undefined") return;
  try {
    // Merge so callers that only pass project ids don't wipe agent identity
    // persisted for Google OAuth returns.
    const existing = readHireCustomPreviewState();
    const merged: HireCustomPreviewSessionState = {
      ...(existing ?? {}),
      ...state,
      agentName: state.agentName ?? existing?.agentName,
      jobTitle: state.jobTitle ?? existing?.jobTitle,
      profileImageUrl: state.profileImageUrl ?? existing?.profileImageUrl,
      agentNameConfirmed: state.agentNameConfirmed ?? existing?.agentNameConfirmed,
    };
    const raw = JSON.stringify(merged);
    sessionStorage.setItem(HIRE_CUSTOM_PREVIEW_SESSION_KEY, raw);
    // Survive Google OAuth full-page redirects / tab restores.
    localStorage.setItem(HIRE_CUSTOM_PREVIEW_SESSION_KEY, raw);
  } catch {
    /* ignore */
  }
}

export function clearHireCustomPreviewState(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(HIRE_CUSTOM_PREVIEW_SESSION_KEY);
    localStorage.removeItem(HIRE_CUSTOM_PREVIEW_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

const stripePaymentCompletionKey = (hireIntentId: string) =>
  `egpt_stripe_payment_complete_${hireIntentId}`;

/** Prevents duplicate completeMarketplaceHire after Stripe success (multiple effects / remounts). */
export function readStripePaymentCompletionState(
  hireIntentId: string,
): "pending" | "done" | null {
  if (typeof window === "undefined" || !hireIntentId.trim()) return null;
  try {
    const value = sessionStorage.getItem(stripePaymentCompletionKey(hireIntentId.trim()));
    if (value === "pending" || value === "done") return value;
    return null;
  } catch {
    return null;
  }
}

export function writeStripePaymentCompletionState(
  hireIntentId: string,
  state: "pending" | "done",
): void {
  if (typeof window === "undefined" || !hireIntentId.trim()) return;
  try {
    sessionStorage.setItem(stripePaymentCompletionKey(hireIntentId.trim()), state);
  } catch {
    /* ignore */
  }
}

export function clearStripePaymentCompletionState(hireIntentId: string): void {
  if (typeof window === "undefined" || !hireIntentId.trim()) return;
  try {
    sessionStorage.removeItem(stripePaymentCompletionKey(hireIntentId.trim()));
  } catch {
    /* ignore */
  }
}

export function resolveStripeReturnHireIntentId(
  params: URLSearchParams,
): string | null {
  const fromUrl = params.get("hireIntentId")?.trim();
  if (fromUrl) return fromUrl;
  const stored = readHireCustomPreviewState();
  return stored?.previewHireIntentId?.trim() || readPendingHireIntentId();
}
